<template>
  <action-menu v-if="showMenu" class="deal-header-menu">
    <template #trigger="{ showMenu }">
      <div
        class="relative ml-2 flex cursor-pointer items-center justify-center rounded-sm border p-2 transition-all duration-300"
        :class="{
          'border-blue-100 bg-blue-100 text-primary': showMenu,
        }"
        data-cy="deal-button-open-menu"
      >
        <icon-base height="24" width="24" :icon="IconVerticalDots" />
      </div>
    </template>

    <action-menu-item
      v-for="option in menuOptions"
      :key="option.label"
      :class="
        option.label === $t('COMMON.DELETE') ? 'text-error' : 'text-gray-600'
      "
      :data-cy="option.dataCy"
      @click="handleAction(option)"
    >
      {{ option.label }}
    </action-menu-item>
  </action-menu>

  <create-new-deal-modal
    v-if="canDuplicateDeal && showNewDealModal"
    :deal="deal"
    :close="() => (showNewDealModal = false)"
  />

  <confirm-modal
    v-if="canSeeDeleteOption && showConfirmModal"
    :description="
      $t('COMMON.DELETE_CONFIRMATION', { source: $t('COMMON.APPLICATION') })
    "
    confirm-button-type="error"
    :confirm-button="$t('COMMON.DELETE')"
    :close="() => (showConfirmModal = false)"
    @confirm="handleDeleteDeal"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import { AnalyticsEvent } from "@/enums/analytics";
import { Ability, PermissionSubject } from "@/enums/auth";
import {
  ADMIN,
  DEAL_MODE_LIVE_KEY,
  DEAL_MODE_TEST_KEY,
  PHASE_ABSTRACT_APPROVED,
  PHASE_COMPLETE,
  STATUS_DEAD,
  SUPER_ADMIN,
  UNDERWRITER,
} from "@/helpers/constants";
import { useAuth, usePermissions } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useActiveWorkflowTemplateStages } from "@/hooks/workflow";
import { useAnalyticsStore } from "@/stores/analytics";

import CreateNewDealModal from "@/components/businesses/CreateNewDealModal.vue";
import IconVerticalDots from "@/components/icons/IconVerticalDots.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuItem from "@/components/ui/ActionMenuItem.vue";
import ActionMenu from "@/components/ui/ActionMenuV2.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IUser } from "@/models/users";

type MenuOption = {
  label: string;
  dataCy?: string;
  action: () => void;
};

const emit = defineEmits<{ rollback: [number] }>();

const deleteRoles = [ADMIN, SUPER_ADMIN, UNDERWRITER] as const;

const { t } = useI18n();
const { dispatch, getters, commit } = useStore();
const router = useRouter();
const copyToClipboard = useClipboardWithMessage();
const { isClientUnderwriter, isLendflowUser, isClientAdmin } = useAuth();
const { trackEvent } = useAnalyticsStore();
const { canPerformActionReactive } = usePermissions();
const {
  activeDeal: deal,
  isActiveDealSelfFunded,
  isActiveDealDataCapture,
} = useDeals();

const {
  activeWorkflowPhases,
  activeTemplateStages,
  activeTemplateFlow,
  indexOfCurrentPhase,
  lastWFLstage,
} = useActiveWorkflowTemplateStages();

const canDuplicateDeal = canPerformActionReactive(
  PermissionSubject.application,
  Ability.duplicate,
  { application: deal.value.id }
);

const canMoveStages = canPerformActionReactive(
  PermissionSubject.application,
  Ability.moveStages,
  { application: deal.value.id }
);

const showConfirmModal = ref(false);
const showNewDealModal = ref(false);

const currentPhase = computed<number>(
  () => getters["applications/currentPhase"]
);
const user = computed<IUser>(() => getters["auth/user"]);

const isNotDeclined = computed(() => {
  return deal.value.status?.toString() !== STATUS_DEAD.toString();
});

const financialRequirementsMet = computed(() => {
  return !(
    deal.value.bank_accounts?.length &&
    (!deal.value.prequalification ||
      parseInt(
        deal.value.prequalification.monthly_avg_revenue?.split("_").pop() || ""
      ) > 1)
  );
});

const showRollbackProgress = computed(() => {
  if (isClientUnderwriter && !isActiveDealSelfFunded.value) {
    return (
      deal.value.status &&
      deal.value.status <= 1 &&
      !isActiveDealDataCapture.value
    );
  }
  return (
    isNotDeclined.value &&
    indexOfCurrentPhase.value > 0 &&
    !isActiveDealDataCapture.value
  );
});

const showCopyResumeLink = computed(
  () => deal.value.id && !currentPhase.value && financialRequirementsMet.value
);

const canSeeDeleteOption = computed(() =>
  Object.values(user.value?.roles || {}).some((role) =>
    deleteRoles.includes(role)
  )
);

const canChangeDealMode = computed(
  () => isLendflowUser || isClientAdmin || isClientUnderwriter
);

const isTestMode = computed(() => deal.value.deal_mode === DEAL_MODE_TEST_KEY);

const showMenu = computed(
  () =>
    showRollbackProgress.value ||
    showCopyResumeLink.value ||
    canSeeDeleteOption.value ||
    canDuplicateDeal.value ||
    canChangeDealMode.value
);

const currentWorkflowPhaseIndx = computed(() =>
  activeTemplateFlow.value.findIndex(
    (stage) => stage.id === deal.value.stage_id
  )
);

const { fetchWrapper: getLink } = usePromiseWrapper(async () => {
  const link = await dispatch("applications/getResumeAppLink");
  await copyToClipboard(link);
});

const { fetchWrapper: changeDealMode } = usePromiseWrapper(async () => {
  const newMode = isTestMode.value ? DEAL_MODE_LIVE_KEY : DEAL_MODE_TEST_KEY;
  await dispatch("applications/updateApplicationDealMode", {
    id: deal.value.id,
    deal_mode: newMode,
  });
});

const { fetchWrapper: rollbackProgress } = usePromiseWrapper(async () => {
  if (!indexOfCurrentPhase.value) {
    return;
  }
  let currentPhaseName =
    activeTemplateFlow.value[currentWorkflowPhaseIndx.value].name;

  if (currentPhaseName === PHASE_COMPLETE) {
    currentPhaseName = PHASE_ABSTRACT_APPROVED;
  }

  emit("rollback", indexOfCurrentPhase.value - 1);
  const newPhaseName =
    activeWorkflowPhases.value[indexOfCurrentPhase.value - 1];

  if (
    lastWFLstage.value.toLocaleLowerCase() === newPhaseName.toLocaleLowerCase()
  ) {
    commit(
      "applications/updateActiveWorkflowStage",
      activeTemplateFlow.value[indexOfCurrentPhase.value].id
    );
    commit(
      "workflows/setActiveStage",
      activeTemplateStages.value[indexOfCurrentPhase.value]
    );
    return;
  }
  await dispatch("workflows/rollbackWorkflowPhase");
  commit(
    "applications/updateActiveWorkflowStage",
    activeTemplateFlow.value[indexOfCurrentPhase.value - 1].id
  );
  dispatch("applications/getWorkflowStatuses", deal.value?.id);

  const newStage = activeTemplateStages.value[indexOfCurrentPhase.value];

  trackEvent(AnalyticsEvent.WflDealStageChanged, {
    applicationId: deal.value.id,
    stageId: newStage.id,
    stageName: newStage.name,
    stageType: newStage.type,
    isRollback: true,
  });
});

const menuOptions = computed(() => {
  const options: Array<MenuOption> = [];
  if (showCopyResumeLink.value) {
    options.push({
      label: t("DEALS.DEAL_PROGRESS.COPY_APPLICATION_LINK"),
      action: getLink,
    });
  }
  if (canMoveStages.value && showRollbackProgress.value) {
    options.push({
      label: t("DEALS.DEAL_PROGRESS.ROLLBACK_PROGRESS"),
      action: rollbackProgress,
    });
  }
  if (canDuplicateDeal.value) {
    options.push({
      label: t("DEALS.NEW_DEAL"),
      dataCy: "menu-new-deal",
      action: () => (showNewDealModal.value = true),
    });
  }
  if (canChangeDealMode.value) {
    options.push({
      label: isTestMode.value
        ? t("DEALS.MODE.MOVE_TO_LIVE")
        : t("DEALS.MODE.MOVE_TO_TEST"),
      dataCy: "menu-change-deal-mode",
      action: changeDealMode,
    });
  }
  if (canSeeDeleteOption.value) {
    options.push({
      label: t("COMMON.DELETE"),
      action: () => (showConfirmModal.value = true),
    });
  }
  return options;
});

const { fetchWrapper: handleDeleteDeal } = usePromiseWrapper(async () => {
  const { id } = deal.value;
  await dispatch("applications/deleteApplication");
  commit("deleteDealFromHistoryStack", { dealId: id });
  router.push({ name: "Deals" });
});

const handleAction = (option: MenuOption) => {
  trackEvent(AnalyticsEvent.DealHeaderMenuOptionSelected, {
    option: option.label,
    deal_id: deal.value.id,
  });
  option.action();
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.deal-header-menu :deep(ul) {
  @apply top-13 right-0 z-100 px-0 py-1;
}

.deal-header-menu :deep(li) {
  @apply rounded-none px-4 py-1;
  &:hover {
    @apply bg-blue-100;
  }
}
</style>
